'use client';

import { useState } from 'react';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { createUserSchema, type CreateUserInput } from '@/lib/validators/user.schema';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useProgramDisplay } from '@/lib/program/use-program-display';
import { TurnstileWidget } from './turnstile-widget';
import { PasswordStrengthMeter } from './password-strength-meter';
import { EmailTypoSuggestion } from './email-typo-suggestion';

// Form input shape — what the form fields actually hold *before* zod transforms.
// The phone field is a plain string at form time; zod transforms it to an
// E.164 branded type at parse time, which we don't carry through to the form's
// react-hook-form generic.
type RegisterFormFields = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  consentAcknowledged: true;
  honeypot?: string;
  turnstileToken: string;
  programSlug?: string;
};

/**
 * The program prefill, resolved ANONYMOUSLY on the server (see the register
 * page). `null` when resolution failed or nothing is configured — the field is
 * then simply not rendered and the POST resolves the program on its own.
 */
export type ProgramChoice = {
  /** The slug the server would stamp if the visitor changed nothing. */
  activeSlug: string | null;
  /** Every self-selectable program, in `(sort_order, name)` order. */
  options: { slug: string; name: string }[];
};

interface RegisterFormProps {
  turnstileSiteKey?: string;
  programChoice?: ProgramChoice | null;
}

export function RegisterForm({ turnstileSiteKey, programChoice }: RegisterFormProps) {
  const t = useTranslations('auth');
  const tc = useTranslations('common');
  const programDisplay = useProgramDisplay();
  // Nothing to choose → no field. Never assume there are exactly two programs.
  const programOptions = programChoice?.options ?? [];
  const showProgramField = programOptions.length > 1;
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<RegisterFormFields>({
    // Cast resolver to a generic-compatible type. The zod schema's output type
    // narrows phone to a branded E.164 string but the form input type uses
    // plain string; this is the standard react-hook-form + zod transform pattern.
    resolver: zodResolver(createUserSchema) as unknown as Resolver<RegisterFormFields>,
    mode: 'onBlur',
    defaultValues: {
      consentAcknowledged: undefined as unknown as true,
      turnstileToken: '',
      // Prefilled with what the server already resolved, so submitting the form
      // untouched reproduces the resolution rather than overriding it.
      programSlug: programChoice?.activeSlug ?? undefined,
    },
  });

  const passwordValue = watch('password') ?? '';
  const emailValue = watch('email') ?? '';

  const onSubmit = async (data: RegisterFormFields) => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data as unknown as CreateUserInput),
      });

      const result = await response.json();

      if (!response.ok) {
        const message =
          result.error?.message ?? t('register.errorGeneric');
        toast.error(message, { duration: Infinity });
        return;
      }

      toast.success(t('register.accountCreated'));

      // Preserve the redirect query param across the verification flow so the
      // user lands back on the original page after they verify and log in.
      const redirect = searchParams.get('redirect');
      const params = new URLSearchParams();
      params.set('email', data.email);
      if (redirect) params.set('redirect', redirect);
      router.push(`/verify-email-sent?${params.toString()}`);
    } catch {
      toast.error(t('register.errorGeneric'), {
        duration: Infinity,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div className="space-y-2">
        <Label htmlFor="firstName" className="text-sm text-[#6b7280]">
          {t('fields.firstName')} <span className="text-[#ef4444]">*</span>
        </Label>
        <Input
          id="firstName"
          type="text"
          placeholder={t('fields.firstNamePlaceholder')}
          aria-describedby={errors.firstName ? 'firstName-error' : undefined}
          aria-invalid={!!errors.firstName}
          className="h-[44px] px-5 text-base focus-visible:ring-[#2fa5fb] focus-visible:ring-2 data-[invalid=true]:border-[#ef4444]"
          data-invalid={!!errors.firstName}
          {...register('firstName')}
        />
        {errors.firstName && (
          <p id="firstName-error" className="text-sm text-[#ef4444]">
            {errors.firstName.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="lastName" className="text-sm text-[#6b7280]">
          {t('fields.lastName')} <span className="text-[#ef4444]">*</span>
        </Label>
        <Input
          id="lastName"
          type="text"
          placeholder={t('fields.lastNamePlaceholder')}
          aria-describedby={errors.lastName ? 'lastName-error' : undefined}
          aria-invalid={!!errors.lastName}
          className="h-[44px] px-5 text-base focus-visible:ring-[#2fa5fb] focus-visible:ring-2 data-[invalid=true]:border-[#ef4444]"
          data-invalid={!!errors.lastName}
          {...register('lastName')}
        />
        {errors.lastName && (
          <p id="lastName-error" className="text-sm text-[#ef4444]">
            {errors.lastName.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="text-sm text-[#6b7280]">
          {t('fields.phone')} <span className="text-[#ef4444]">*</span>
        </Label>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <PhoneInput
              international
              defaultCountry="US"
              id="phone"
              placeholder={t('fields.phonePlaceholder')}
              value={field.value}
              onChange={(value) => field.onChange(value ?? '')}
              onBlur={field.onBlur}
              className="phone-input h-[44px]"
            />
          )}
        />
        {errors.phone && (
          <p id="phone-error" className="text-sm text-[#ef4444]">
            {errors.phone.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-sm text-[#6b7280]">
          {tc('email')} <span className="text-[#ef4444]">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          placeholder={t('fields.emailPlaceholder')}
          aria-describedby={errors.email ? 'email-error' : undefined}
          aria-invalid={!!errors.email}
          className="h-[44px] px-5 text-base focus-visible:ring-[#2fa5fb] focus-visible:ring-2 data-[invalid=true]:border-[#ef4444]"
          data-invalid={!!errors.email}
          {...register('email')}
        />
        <EmailTypoSuggestion
          email={emailValue}
          onAccept={(suggested) =>
            setValue('email', suggested, { shouldValidate: true })
          }
        />
        {errors.email && (
          <p id="email-error" className="text-sm text-[#ef4444]">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password" className="text-sm text-[#6b7280]">
          {t('fields.password')} <span className="text-[#ef4444]">*</span>
        </Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder={t('fields.passwordPlaceholder')}
            aria-describedby={errors.password ? 'password-error' : undefined}
            aria-invalid={!!errors.password}
            className="h-[44px] px-5 pr-12 text-base focus-visible:ring-[#2fa5fb] focus-visible:ring-2 data-[invalid=true]:border-[#ef4444]"
            data-invalid={!!errors.password}
            {...register('password')}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? t('hidePassword') : t('showPassword')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-[#374151]"
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        <PasswordStrengthMeter password={passwordValue} />
        {errors.password && (
          <p id="password-error" className="text-sm text-[#ef4444]">
            {errors.password.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword" className="text-sm text-[#6b7280]">
          {t('fields.confirmPassword')} <span className="text-[#ef4444]">*</span>
        </Label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder={t('fields.confirmPasswordPlaceholder')}
            aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
            aria-invalid={!!errors.confirmPassword}
            className="h-[44px] px-5 pr-12 text-base focus-visible:ring-[#2fa5fb] focus-visible:ring-2 data-[invalid=true]:border-[#ef4444]"
            data-invalid={!!errors.confirmPassword}
            {...register('confirmPassword')}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword((v) => !v)}
            aria-label={showConfirmPassword ? t('hidePassword') : t('showPassword')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-[#374151]"
          >
            {showConfirmPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p id="confirmPassword-error" className="text-sm text-[#ef4444]">
            {errors.confirmPassword.message}
          </p>
        )}
      </div>

      {/*
        Program choice. Rendered only when there is genuinely something to
        choose — one selectable program means no decision to make, and the POST
        resolves the same answer on its own.

        ⚠️ Accessibility: the project `Select` is Radix, not a native `<select>`,
        so a `<Label htmlFor>` does NOT associate with the trigger. The trigger
        is wired by `aria-labelledby` / `aria-describedby` instead, and each
        option carries an explicit `textValue` so its tagline stays out of the
        accessible name.
      */}
      {showProgramField && (
        <div className="space-y-2">
          <span id="programSlug-label" className="block text-sm text-[#6b7280]">
            {t('register.programLabel')}
          </span>
          <Controller
            name="programSlug"
            control={control}
            render={({ field }) => {
              const selected = programOptions.find((p) => p.slug === field.value);
              const selectedDisplay = selected
                ? programDisplay(selected.slug, selected.name)
                : null;
              return (
                <>
                  <Select value={field.value ?? undefined} onValueChange={field.onChange}>
                    <SelectTrigger
                      className="h-[44px] w-full"
                      aria-labelledby="programSlug-label"
                      aria-describedby="programSlug-help"
                    >
                      {selectedDisplay ? (
                        <SelectValue>{selectedDisplay.name}</SelectValue>
                      ) : (
                        <SelectValue placeholder={t('register.programLabel')} />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {programOptions.map((p) => {
                        const d = programDisplay(p.slug, p.name);
                        return (
                          <SelectItem key={p.slug} value={p.slug} textValue={d.name}>
                            <span className="block">{d.name}</span>
                            {d.tagline && (
                              <span
                                aria-hidden="true"
                                className="mt-0.5 block text-xs text-[#6b7280]"
                              >
                                {d.tagline}
                              </span>
                            )}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {selectedDisplay?.tagline && (
                    <p className="text-xs text-[#6b7280]">{selectedDisplay.tagline}</p>
                  )}
                </>
              );
            }}
          />
          <p id="programSlug-help" className="text-xs text-[#6b7280]">
            {t('register.programHelp')}
          </p>
        </div>
      )}

      <label
        htmlFor="consentAcknowledged"
        className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 cursor-pointer"
      >
        <input
          id="consentAcknowledged"
          type="checkbox"
          {...register('consentAcknowledged')}
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
        />
        <span className="text-xs leading-relaxed text-[#374151]">
          {t.rich('register.consent', {
            terms: (chunks) => (
              <a href="/terms" className="text-[#2fa5fb] underline">
                {chunks}
              </a>
            ),
            privacy: (chunks) => (
              <a href="/privacy" className="text-[#2fa5fb] underline">
                {chunks}
              </a>
            ),
          })}
        </span>
      </label>
      {errors.consentAcknowledged && (
        <p className="text-sm text-[#ef4444]">{errors.consentAcknowledged.message}</p>
      )}

      {/* Honeypot — hidden from sighted users and assistive tech */}
      <input
        type="text"
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        {...register('honeypot')}
      />

      <div className="flex justify-center">
        <TurnstileWidget
          siteKey={turnstileSiteKey}
          onVerify={(token) => setValue('turnstileToken', token, { shouldValidate: true })}
          onExpire={() => setValue('turnstileToken', '')}
          onError={() => setValue('turnstileToken', '')}
        />
      </div>
      {errors.turnstileToken && (
        <p className="text-sm text-[#ef4444]">{errors.turnstileToken.message}</p>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        className="h-[44px] w-full bg-[#fc800a] text-white font-bold hover:bg-[#e07209] disabled:opacity-40 disabled:bg-[#fc800a]"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            <span className="sr-only">{t('register.submittingSr')}</span>
            {t('register.submitting')}
          </>
        ) : (
          t('register.submit')
        )}
      </Button>
    </form>
  );
}
